import {CheckOutlined} from '@ant-design/icons'
import {Avatar, Button, List, Spin} from 'antd'
import {auth_getOwnedItems_Api} from 'apis'
import {getType} from 'apis/model/base'
import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import {ObjectSelectField} from 'components/form/fields/ObjectSelectField'
import {createValue} from 'components/form/utils'
import {LoginContext} from 'components/LoginContext'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {FaUserTie} from 'react-icons/fa6'
import {IoSearchOutline} from 'react-icons/io5'
import {branch, compose, fromRenderProps, renderComponent} from 'recompose'
import {UnauthenticatedAlert} from 'views/AppLocals/AppAlert'
import {AppEntities} from 'views/AppLocals/enums'
import {getResponseItems, NullAsDefault, renderOwnChild, renderSelf,} from 'views/Shared'
import {renderElse} from '../../Shared'
import AntdConfigWrapper from './AntdConfigWrapper'
import './ContainerPicker.css'

export const renderOwnerOption = (option, actions, params = {}) => {
  const title = getTitle(option)
  const verified = _.get(option, 'certified')

  const _type =
    _.get(option, '_type') === 'course' ||
    _.get(option, '_type') === 'course_class'
  const type = _.get(option, '_type') === 'organization'

  // const phone = _.get(option, 'phone_number')
  // const email = _.get(option, 'email')
  // const address = getFullAddress(_.get(option, 'location'))
  //
  // const Contact = () => {
  //   if (_.isEmpty(phone) && _.isEmpty(email) && _.isEmpty(address)) {
  //     return <Translate>{(t) => t(_.get(option, '_type'))}</Translate>
  //   } else if (_.isEmpty(phone) && _.isEmpty(email) && address) {
  //     return <span>{address}</span>
  //   } else if (_.isEmpty(phone) || _.isEmpty(email)) {
  //     return <span>{phone || email}</span>
  //   }
  //   return [phone, email].join(' - ')
  // }

  return (
    <Translate>
      {(t) => (
        <List.Item
          className="p-0 h-full custom-container-picker"
          actions={actions}>
          <List.Item.Meta
            title={
              <div className="flex flex-col flex-1 truncate">
                <span className="font-bold truncate text-sm text-color-000">
                  {title}
                </span>
                <span className="text-2xs italic text-color-500 truncate">
                  {t(_.get(option, '_type'))}
                </span>
              </div>
            }
            className="flex items-center gap-2"
            avatar={renderElse(
              _type,
              <Avatar
                size={30}
                shape={type ? 'square' : 'circle'}
                icon={
                  <FaUserTie
                    size={14}
                    className="text-color-300"
                  />
                }
                src={_.get(option, 'avatar') || _.get(option, 'owner.avatar')}
                className={classNames(
                  'flex flex-center background-200',
                  type && 'rounded-md'
                )}
              />
            )}
            {...params}
          />
        </List.Item>
      )}
    </Translate>
  )
}

const ContainerPicker = ({
  name,
  label,
  options,
  optionParams,
  defaultValue,
  dependencies,
  customConfigs,
  onOptionChange,
  onChange = NullAsDefault,
  renderInnerWrapper,
  allowClear = false,
  optionIdProp = 'id',
  getOption = renderSelf,
  Wrapper = renderOwnChild,
  placeholder = 'click to select',
  renderOption = renderOwnerOption,
  optionLabelProp = 'selectedOption',
  renderActions = (actions, option) => actions,
  withResponses = (data, params) => data,
  mapCustomProps = (props, optionIdProp) => props,
  type = AppEntities.ORGANIZATION,
}) => {
  const asyncConfigs = useMemo(() => {
    switch (type) {
      case AppEntities.UNDEFINED:
        return customConfigs
      case AppEntities.ORGANIZATION:
      default:
        return {
          query: {
            ':types': [AppEntities.USER].join(','),
          },
          apiInfo: auth_getOwnedItems_Api,
        }
    }
  }, [type, customConfigs])

  const defaultOptions = Array.from(options || [])

  const t = useTranslate()

  const tranform = useCallback(
    (options = [], defaultValue = []) => {
      if (_.isEmpty(options)) {
        return defaultValue
      }
      return options
        .filter((item) => !_.isEmpty(getOption(item)))
        .map((option) => {
          const _option = getOption(option)
          const value = _.get(_option, optionIdProp)
          const actions = renderActions(
            [
              <Button
                type="primary"
                size="small"
                className="rounded-md no-shadow no-text-shadow text-xs">
                {t('choose')}
              </Button>,
            ],
            _option,
            { optionIdProp }
          )
          return mapCustomProps(
            {
              value,
              id: value,
              option: _option,
              [optionIdProp]: value,
              style: { height: 50 },
              className: 'items-center',
              type: getType(_option),
              [optionLabelProp]: renderOption(_option, undefined, optionParams),
              label: renderOption(_option, actions, optionParams),
              title: getTitle(_option),
            },
            optionIdProp
          )
        })
    },
    [t, renderActions, optionIdProp]
  )

  const { apiInfo, query, values } = asyncConfigs || {}

  const [isLoaded, setLoaded] = useState(false)


  const {
    isLoading = false,
    response: searchResponse,
    handleAsyncAction: search,
  } = useAsyncAction({
    query,
    values,
    apiInfo,
    onSuccess: () => {
      setLoaded(true)
    },
  })

  useEffect(() => {
    if (apiInfo && !isLoaded) {
      search()
    }
  }, [apiInfo, isLoaded])

  const handleSearch = _.debounce((keyword) => {
    search({ keyword })
  }, 300)

  const RenderEmpty = () =>
    isLoading ? (
      <Spin
        spinning={true}
        className="flex items-center justify-center"
      />
    ) : (
      <EmptyHolder />
    )

  const render = useCallback(
    (dataSource = []) => {
      const _options = Array.from(
        tranform(
          withResponses(dataSource, {
            optionIdProp,
          }),
          defaultOptions
        )
      )
      const _defaultValue = _options
        .map(({ value }) => value)
        .includes(defaultValue)
        ? defaultValue
        : undefined

      return (
        <ObjectSelectField
          name={name}
          dependencies={dependencies}
          Wrapper={renderInnerWrapper}
          key={[isLoaded, defaultValue]}
          placeholder={isLoading ? '...' : t(placeholder)}
          onChange={(event) => {
            const { name, value } = event.target
            onChange(createValue(name, value === undefined ? null : value))
          }}
          onOptionChange={onOptionChange}
          label={label ? t(label) : undefined}
          // bordered={false}
          variant="borderless"
          suffixIcon={isLoading ? undefined : <IoSearchOutline />}
          allowClear={allowClear}
          onSearch={handleSearch}
          filterOption={false}
          showSearch={true}
          options={isLoading ? [] : _options}
          optionFilterProp="title"
          defaultValue={isLoading ? undefined : _defaultValue}
          loading={!!isLoading}
          optionLabelProp={optionLabelProp}
        />
      )
    },
    [
      t,
      name,
      onChange,
      tranform,
      isLoading,
      withResponses,
      optionIdProp,
      handleSearch,
      defaultValue,
      defaultOptions,
      searchResponse,
    ]
  )

  // const cacheId = [
  //   apiInfo.path,
  //   JSON.stringify(query),
  //   JSON.stringify(values),
  // ].join('/')

  return (
    <Wrapper>
      <AntdConfigWrapper renderEmpty={RenderEmpty}>
        {render(apiInfo ? getResponseItems(searchResponse) : defaultOptions)}
      </AntdConfigWrapper>
    </Wrapper>
  )
}

export const renderDefaultActions =
  (value, params) => (actions, option, params) => {
    const { optionIdProp } = params || {}
    const { ActiveComponent = CheckOutlined } = params || {}
    return _.get(option, optionIdProp) === _.get(value, optionIdProp, value)
      ? [
          <ActiveComponent
            style={{ color: '#87d068' }}
            className="p-2"
          />,
        ]
      : actions
  }

export const withOwnerResponses = (default_object) =>
  Boolean(_.isEmpty(default_object) || !_.isObject(default_object))
    ? undefined
    : (items = [], { optionIdProp }) => [
        {
          owner: default_object,
        },
        ...Array.from(items || []).filter(
          ({ owner }) =>
            _.get(owner, optionIdProp) !== _.get(default_object, optionIdProp)
        ),
      ]

export default compose(
  fromRenderProps(LoginContext.Consumer, (login) => ({ authorized: !!login })),
  branch(
    ({ authorized }) => !!!authorized,
    renderComponent(UnauthenticatedAlert)
  )
)(ContainerPicker)
